/**
 * System session tuning (M3 plan R12): `src/data/reach-session.json`, Zod-validated here. Hail range and speed gate,
 * the hail point, the camera far plane, the respawn fade, the planet guard margin, the safe-space radius and the looks
 * of faction-less structures. Every value is a starting value for playtest; the runtime's "Session" tuning group
 * exposes the live ones.
 */
import { z } from 'zod'
import sessionJson from '../../data/reach-session.json'
import { FACTION_IDS, type FactionId } from '../../render/contracts'

const positive = z.number().positive()
const nonNegative = z.number().min(0)
const look = z.enum(FACTION_IDS as unknown as [FactionId, ...FactionId[]])

export const sessionConfigSchema = z.strictObject({
  comment: z.string().optional(),
  hail: z.strictObject({ rangeM: positive, maxSpeed: positive, hailPointM: nonNegative }),
  cameraFarM: positive,
  respawn: z.strictObject({ fadeS: nonNegative }),
  planetGuard: z.strictObject({ marginM: nonNegative }),
  safe: z.strictObject({ hostileRadiusM: nonNegative }),
  cellCandidateSlackM: nonNegative,
  looks: z.strictObject({
    relay: look,
    poi: z.strictObject({ derelict: look, cache: look, anomaly: look, 'nav-beacon': look, 'hidden-base': look }),
    guardianBase: look,
  }),
})

export type SessionConfig = z.infer<typeof sessionConfigSchema>

/** Parses and validates reach-session.json (throws a ZodError naming the bad field). */
export function loadSessionConfig(raw: unknown = sessionJson): SessionConfig {
  return sessionConfigSchema.parse(raw)
}
